import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, GitCompare, Loader2, RefreshCw, Sparkles, X } from 'lucide-react';
import api from '../../api';
import AiDisclaimerCard from '../AiDisclaimerCard';

const keywordKey = value => String(value || '').trim().toLowerCase();

const isExactOrTokenMatch = (skillA, skillB) => {
  const a = keywordKey(skillA);
  const b = keywordKey(skillB);
  if (!a || !b) return false;
  if (a === b) return true;

  // Handle alternatives with " or " e.g. "Python or Bash scripting"
  if (b.includes(' or ')) {
    const parts = b.split(/\s+or\s+/).map(p => p.trim()).filter(Boolean);
    if (parts.some(p => isExactOrTokenMatch(a, p))) return true;
  }
  if (a.includes(' or ')) {
    const parts = a.split(/\s+or\s+/).map(p => p.trim()).filter(Boolean);
    if (parts.some(p => isExactOrTokenMatch(p, b))) return true;
  }

  // Handle parenthetical aliases like "Google Cloud Platform (GCP)"
  const parenMatchB = b.match(/\((.*?)\)/);
  if (parenMatchB && parenMatchB[1] && parenMatchB[1].trim() === a) return true;
  const parenMatchA = a.match(/\((.*?)\)/);
  if (parenMatchA && parenMatchA[1] && parenMatchA[1].trim() === b) return true;

  // Whole word/phrase match (never substring like "ci" in "artificial")
  const escapeRegex = s => s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  if (a.length >= 3 && new RegExp(`\\b${escapeRegex(a)}\\b`, 'i').test(b)) return true;
  if (b.length >= 3 && new RegExp(`\\b${escapeRegex(b)}\\b`, 'i').test(a)) return true;

  return false;
};

const containsKeyword = (matches, requirement) => {
  if (!matches || !matches.length || !requirement) return false;
  return matches.some(match => isExactOrTokenMatch(match, requirement));
};

const RequirementMatrix = ({ requirements, candidateA, candidateB }) => (
  <div style={{ overflowX: 'auto', marginTop: '16px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '560px' }}>
      <thead><tr style={{ background: 'var(--faculty-bg)', borderBottom: '1px solid var(--faculty-border)' }}>
        <th style={{ padding: '10px', textAlign: 'left' }}>Company requirement</th>
        <th style={{ padding: '10px', textAlign: 'center' }}>Type</th>
        <th style={{ padding: '10px', textAlign: 'center' }}>{candidateA.candidate_name || 'Candidate A'}</th>
        <th style={{ padding: '10px', textAlign: 'center' }}>{candidateB.candidate_name || 'Candidate B'}</th>
      </tr></thead>
      <tbody>
        {[
          ...(requirements.must_have_skills || []).map(skill => ({ skill, type: 'Must-have' })),
          ...(requirements.good_to_have_skills || []).map(skill => ({ skill, type: 'Optional' })),
        ].map(({ skill, type }) => {
          const matchedA = type === 'Must-have' ? containsKeyword(candidateA.matched_skills || [], skill) : containsKeyword(candidateA.matched_good_to_have || [], skill);
          const matchedB = type === 'Must-have' ? containsKeyword(candidateB.matched_skills || [], skill) : containsKeyword(candidateB.matched_good_to_have || [], skill);
          return <tr key={`${type}-${skill}`} style={{ borderBottom: '1px solid var(--faculty-border)' }}>
            <td style={{ padding: '10px', fontWeight: 700 }}>{skill}</td>
            <td style={{ padding: '10px', textAlign: 'center', color: type === 'Must-have' ? '#b45309' : 'var(--faculty-muted)' }}>{type}</td>
            <td style={{ padding: '10px', textAlign: 'center' }}>{matchedA ? <CheckCircle2 size={18} color="#059669" aria-label="Matched" /> : <X size={18} color="#b91c1c" aria-label="Not matched" />}</td>
            <td style={{ padding: '10px', textAlign: 'center' }}>{matchedB ? <CheckCircle2 size={18} color="#059669" aria-label="Matched" /> : <X size={18} color="#b91c1c" aria-label="Not matched" />}</td>
          </tr>;
        })}
      </tbody>
    </table>
  </div>
);

const CandidateRankingPanel = ({ jobId, roleName, isInternalJob = false, onSelectCandidate = null }) => {
  const [ranking, setRanking] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [explainingId, setExplainingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [error, setError] = useState('');

  // AI Disclaimer gate
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerFeature, setDisclaimerFeature] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  const requireDisclaimer = (featureName, action) => {
    if (sessionStorage.getItem('ai_disclaimer_agreed')) {
      action();
    } else {
      setDisclaimerFeature(featureName);
      setPendingAction(() => action);
      setShowDisclaimer(true);
    }
  };

  const loadRanking = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError('');
    setComparison(null);
    try {
      const response = await api.get(`/tpo/matching/jobs/${jobId}/ranking`, {
        params: { page: 1, page_size: 100, is_internal: isInternalJob }
      });
      setRanking(response.data);
      const [configResponse, analyticsResponse] = await Promise.all([
        api.get(`/tpo/matching/jobs/${jobId}/config`, { params: { is_internal: isInternalJob } }),
        api.get(`/tpo/matching/jobs/${jobId}/analytics`, { params: { is_internal: isInternalJob } }),
      ]);
      setConfig(configResponse.data);
      setAnalytics(analyticsResponse.data);
      setSelectedIds([]);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Candidate matching is currently unavailable.');
    } finally {
      setLoading(false);
    }
  }, [jobId, isInternalJob]);

  useEffect(() => {
    loadRanking();
  }, [loadRanking]);

  const toggleCandidate = (applicationId) => {
    setSelectedIds(current => current.includes(applicationId)
      ? current.filter(id => id !== applicationId)
      : current.length < 2 ? [...current, applicationId] : current);
    setComparison(null);
  };

  const compareCandidates = () => {
    requireDisclaimer('AI Candidate Comparison', () => _runCompareCandidates());
  };

  const _runCompareCandidates = async () => {
    if (selectedIds.length !== 2 || !jobId) return;
    setComparing(true);
    setError('');
    try {
      const response = await api.get(`/tpo/matching/jobs/${jobId}/compare`, {
        params: { application_a: selectedIds[0], application_b: selectedIds[1], is_internal: isInternalJob }
      });
      setComparison(response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to compare these candidates.');
    } finally {
      setComparing(false);
    }
  };

  const explainCandidate = (applicationId) => {
    requireDisclaimer('AI Candidate Explanation', () => _runExplainCandidate(applicationId));
  };

  const _runExplainCandidate = async (applicationId) => {
    setExplainingId(applicationId);
    setError('');
    try {
      const response = await api.post(`/tpo/matching/jobs/${jobId}/applications/${applicationId}/explanation`, null, {
        params: { is_internal: isInternalJob }
      });
      setRanking(current => current ? {
        ...current,
        candidates: current.candidates.map(candidate => candidate.application_id === applicationId ? { ...candidate, ...response.data } : candidate),
      } : current);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to generate the candidate explanation.');
    } finally {
      setExplainingId(null);
    }
  };

  if (!jobId) return null;

  return (
    <section style={{ background: '#fff', border: '1px solid var(--faculty-border)', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ margin: 0, color: 'var(--faculty-text)', fontSize: '1.05rem' }}>Candidate Fit Ranking</h4>
          <p style={{ margin: '6px 0 0', color: 'var(--faculty-muted)', fontSize: '0.85rem' }}>
            Ranked for {roleName}. Fit is based on transparent keyword matches from the company requirements.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button type="button" onClick={loadRanking} disabled={loading} title="Refresh ranking" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid var(--faculty-border)', background: '#fff', color: 'var(--faculty-text)', borderRadius: '8px', padding: '8px 12px', cursor: loading ? 'not-allowed' : 'pointer', fontWeight: 700 }}><RefreshCw size={15} className={loading ? 'spin' : ''} /> Refresh</button>
        </div>
      </div>

      {error && <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '16px', padding: '12px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', fontSize: '0.85rem' }}><AlertTriangle size={16} /> {error}</div>}
      {loading && <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0', color: 'var(--faculty-muted)' }}><Loader2 size={18} className="spin" /> Calculating candidate fit...</div>}
      {!loading && ranking && ranking.candidates.length === 0 && <p style={{ margin: '20px 0 0', color: 'var(--faculty-muted)', fontSize: '0.9rem' }}>No candidates are available for matching.</p>}

      {!loading && ranking?.candidates?.length > 0 && (
        <>
          {analytics && <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', marginTop: '16px', padding: '12px 14px', background: 'var(--faculty-bg)', borderRadius: '8px', color: 'var(--faculty-muted)', fontSize: '0.82rem' }}><span><strong>{analytics.total_candidates}</strong> candidates</span><span>Matching: <strong>{config?.scoring_version || 'keyword count'}</strong></span></div>}
          <div style={{ overflowX: 'auto', marginTop: '16px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '980px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--faculty-border)', background: 'var(--faculty-bg)' }}>
                  <th style={{ padding: '10px', textAlign: 'center', width: '60px' }}>Compare</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Rank</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Candidate</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Fit</th>
                  <th style={{ padding: '10px', textAlign: 'left' }}>Gaps</th>
                </tr>
              </thead>
              <tbody>
                {ranking.candidates.map(candidate => (
                  <tr key={candidate.application_id} style={{ borderBottom: '1px solid var(--faculty-border)' }}>
                    <td style={{ padding: '10px', textAlign: 'center' }}><input type="checkbox" style={{ width: '16px', height: '16px', margin: 0, padding: 0, cursor: 'pointer' }} checked={selectedIds.includes(candidate.application_id)} onChange={() => toggleCandidate(candidate.application_id)} aria-label={`Compare ${candidate.candidate_name}`} /></td>
                    <td style={{ padding: '10px', fontWeight: 800, color: 'var(--faculty-accent)' }}>#{candidate.rank}</td>
                    <td style={{ padding: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <strong
                          style={onSelectCandidate ? { cursor: 'pointer', color: 'var(--faculty-accent)' } : {}}
                          onClick={() => onSelectCandidate && onSelectCandidate(candidate.application_id)}
                          title={onSelectCandidate ? "View Candidate Details" : undefined}
                        >
                          {candidate.candidate_name}
                        </strong>
                        {onSelectCandidate && (
                          <button
                            type="button"
                            onClick={() => onSelectCandidate(candidate.application_id)}
                            style={{
                              border: '1px solid var(--faculty-border)',
                              background: 'var(--faculty-bg)',
                              color: 'var(--faculty-accent)',
                              borderRadius: '4px',
                              padding: '2px 8px',
                              fontSize: '0.72rem',
                              fontWeight: 700,
                              cursor: 'pointer'
                            }}
                          >
                            Details →
                          </button>
                        )}
                      </div>
                      <button type="button" onClick={() => explainCandidate(candidate.application_id)} disabled={explainingId === candidate.application_id} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '7px', border: 'none', background: 'transparent', color: 'var(--faculty-accent)', padding: 0, cursor: explainingId === candidate.application_id ? 'wait' : 'pointer', fontSize: '0.76rem', fontWeight: 700 }}><Sparkles size={13} /> {explainingId === candidate.application_id ? 'Explaining...' : candidate.explanation ? 'Refresh explanation' : 'Explain fit'}</button>
                      {candidate.explanation && (
                        <div style={{ marginTop: '10px', padding: '12px 14px', background: 'var(--faculty-bg)', borderRadius: '8px', color: 'var(--faculty-muted)', fontSize: '0.78rem', lineHeight: 1.5 }}>
                          <strong style={{ color: 'var(--faculty-text)' }}>Why this fit</strong>
                          <div style={{ marginTop: '4px' }}>{candidate.explanation}</div>

                          {/* Candidate Skills */}
                          <div style={{ marginTop: '12px', fontWeight: 700, color: 'var(--faculty-text)' }}>Candidate Skills</div>
                          <div style={{ marginTop: '4px' }}>
                            {(() => {
                              const candidateSkillsList = (candidate.candidate_skills && candidate.candidate_skills.length > 0)
                                ? candidate.candidate_skills
                                : (candidate.matched_skills && candidate.matched_skills.length > 0)
                                  ? candidate.matched_skills
                                  : [];
                              const matchedJobSkills = [
                                ...(candidate.matched_must_have || candidate.matched_skills || []),
                                ...(candidate.matched_good_to_have || [])
                              ];

                              if (!candidateSkillsList.length) {
                                return <span style={{ color: 'var(--faculty-muted)', fontStyle: 'italic', fontSize: '0.75rem' }}>No profile skills found</span>;
                              }

                              return candidateSkillsList.map(skill => {
                                const isEligible = containsKeyword(matchedJobSkills, skill);
                                return (
                                  <span
                                    key={skill}
                                    style={{
                                      display: 'inline-block',
                                      margin: '4px 4px 0 0',
                                      padding: '3px 8px',
                                      borderRadius: '999px',
                                      fontSize: '0.74rem',
                                      fontWeight: isEligible ? 700 : 500,
                                      background: isEligible ? '#dcfce7' : '#f1f5f9',
                                      color: isEligible ? '#166534' : '#475569',
                                      border: isEligible ? '1px solid #86efac' : '1px solid #cbd5e1'
                                    }}
                                    title={isEligible ? 'Skill matches role requirement (eligible)' : 'Candidate profile skill'}
                                  >
                                    {isEligible ? '✓ ' : '• '}{skill}
                                  </span>
                                );
                              });
                            })()}
                          </div>

                          {/* Must Have Skills */}
                          <div style={{ marginTop: '12px', fontWeight: 700, color: 'var(--faculty-text)' }}>Must Have Skills</div>
                          <div style={{ marginTop: '4px' }}>
                            {(() => {
                              const mustHaveList = candidate.requirement_profile?.must_have_skills || ranking?.requirement_profile?.must_have_skills || [];
                              if (!mustHaveList.length) {
                                return <span style={{ color: 'var(--faculty-muted)', fontStyle: 'italic', fontSize: '0.75rem' }}>No must-have skills specified for this role</span>;
                              }
                              return mustHaveList.map(skill => {
                                const isMatched = containsKeyword(candidate.matched_must_have || candidate.matched_skills || [], skill);
                                return (
                                  <span
                                    key={skill}
                                    style={{
                                      display: 'inline-block',
                                      margin: '4px 4px 0 0',
                                      padding: '3px 8px',
                                      borderRadius: '999px',
                                      fontSize: '0.74rem',
                                      fontWeight: 600,
                                      background: isMatched ? '#dcfce7' : '#fee2e2',
                                      color: isMatched ? '#166534' : '#991b1b',
                                      border: `1px solid ${isMatched ? '#86efac' : '#fecaca'}`
                                    }}
                                  >
                                    {isMatched ? '✓ ' : '✕ '}{skill}
                                  </span>
                                );
                              });
                            })()}
                          </div>

                          {/* Optional Skills */}
                          <div style={{ marginTop: '12px', fontWeight: 700, color: 'var(--faculty-text)' }}>Optional Skills</div>
                          <div style={{ marginTop: '4px' }}>
                            {(() => {
                              const optionalList = candidate.requirement_profile?.good_to_have_skills || ranking?.requirement_profile?.good_to_have_skills || [];
                              if (!optionalList.length) {
                                return <span style={{ color: 'var(--faculty-muted)', fontStyle: 'italic', fontSize: '0.75rem' }}>No optional skills specified for this role</span>;
                              }
                              return optionalList.map(skill => {
                                const isMatched = containsKeyword(candidate.matched_good_to_have || [], skill);
                                return (
                                  <span
                                    key={skill}
                                    style={{
                                      display: 'inline-block',
                                      margin: '4px 4px 0 0',
                                      padding: '3px 8px',
                                      borderRadius: '999px',
                                      fontSize: '0.74rem',
                                      fontWeight: isMatched ? 600 : 500,
                                      background: isMatched ? '#dcfce7' : '#f1f5f9',
                                      color: isMatched ? '#166534' : 'var(--faculty-muted)',
                                      border: isMatched ? '1px solid #86efac' : '1px solid #e2e8f0'
                                    }}
                                  >
                                    {isMatched ? '✓ ' : '• '}{skill}
                                  </span>
                                );
                              });
                            })()}
                          </div>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px' }}><strong style={{ color: '#059669' }}>{candidate.overall_score} / {(ranking.requirement_profile.must_have_skills || []).length + (ranking.requirement_profile.good_to_have_skills || []).length}</strong><div style={{ color: 'var(--faculty-muted)', fontSize: '0.76rem' }}>keywords matched</div></td>
                    <td style={{ padding: '10px', color: candidate.missing_skills.length ? '#b45309' : '#059669' }}>{candidate.missing_skills.length ? candidate.missing_skills.join(', ') : 'None'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
            <button type="button" onClick={compareCandidates} disabled={selectedIds.length !== 2 || comparing} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: 'none', borderRadius: '8px', background: selectedIds.length === 2 ? 'var(--faculty-accent)' : '#cbd5e1', color: '#fff', cursor: selectedIds.length === 2 && !comparing ? 'pointer' : 'not-allowed', fontWeight: 700 }}>
              <GitCompare size={16} /> {comparing ? 'Comparing...' : 'Compare selected'}
            </button>
            {selectedIds.length > 0 && <button type="button" onClick={() => { setSelectedIds([]); setComparison(null); }} title="Clear selection" style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', border: 'none', background: 'transparent', color: 'var(--faculty-muted)', cursor: 'pointer', fontWeight: 700 }}><X size={15} /> Clear</button>}
          </div>
        </>
      )}

      {comparison && (
        <div style={{ marginTop: '20px', borderTop: '1px solid var(--faculty-border)', paddingTop: '18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
            {[comparison.application_a, comparison.application_b].map((candidate, index) => (
              <div key={candidate.application_id} style={{ border: '1px solid var(--faculty-border)', borderRadius: '10px', padding: '14px', background: ((index === 0 && comparison.winner === 'application_a') || (index === 1 && comparison.winner === 'application_b')) ? '#ecfdf5' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}><strong>{candidate.candidate_name}</strong>{((index === 0 && comparison.winner === 'application_a') || (index === 1 && comparison.winner === 'application_b')) && <CheckCircle2 size={17} color="#059669" />}</div>
                <div style={{ fontSize: '1.7rem', fontWeight: 800, color: '#059669', marginTop: '8px' }}>{candidate.overall_score} / {(comparison.requirement_profile.must_have_skills || []).length + (comparison.requirement_profile.good_to_have_skills || []).length}</div>
                <div style={{ marginTop: '8px', fontSize: '0.82rem', color: candidate.missing_skills.length ? '#b45309' : '#059669' }}>Gaps: {candidate.missing_skills.length ? candidate.missing_skills.join(', ') : 'None'}</div>
              </div>
            ))}
          </div>
          <h5 style={{ margin: '20px 0 0', color: 'var(--faculty-text)', fontSize: '0.95rem' }}>Requirement-by-requirement comparison</h5>
          <RequirementMatrix requirements={comparison.requirement_profile} candidateA={comparison.application_a} candidateB={comparison.application_b} />
          <ul style={{ margin: '14px 0 0', paddingLeft: '20px', color: 'var(--faculty-muted)', fontSize: '0.85rem' }}>{comparison.comparison_summary.map(item => <li key={item}>{item}</li>)}</ul>
        </div>
      )}

      {/* AI Disclaimer Modal */}
      {showDisclaimer && (
        <AiDisclaimerCard
          featureName={disclaimerFeature}
          onAgree={() => {
            sessionStorage.setItem('ai_disclaimer_agreed', '1');
            setShowDisclaimer(false);
            if (pendingAction) {
              pendingAction();
              setPendingAction(null);
            }
          }}
          onCancel={() => {
            setShowDisclaimer(false);
            setPendingAction(null);
          }}
        />
      )}
    </section>
  );
};

export default CandidateRankingPanel;
